import Canvas, { CanvasProps } from './Canvas';
import { CircleCanvas } from './CircleCanvas';
import GradientCanvas, { ColorStop } from './GradientCanvas';

export type LatLngIntensity = [number, number, number];

interface HeatmapCanvasProps extends CanvasProps {
  cellWidth: number;
}

export class HeatmapCanvas extends Canvas {
  private readonly _circle: CircleCanvas;
  private readonly _gradient: ImageData | null;
  private _data: LatLngIntensity[];

  static MIN_OPACITY = 0.2;
  static MAX_INTENSITY = 1;
  static MIN_CELL_WIDTH = 30;

  constructor({ width, height, cellWidth, className }: HeatmapCanvasProps) {
    super({ width, height, className });

    this._data = [];
    this._gradient = this.getGradient();
    this._circle = new CircleCanvas(cellWidth);
  }

  public setData(data: LatLngIntensity[]): void {
    this._data = data;
  }

  public setCellWidth(cellWidth: number): void {
    const safeWidth = Math.max(cellWidth, HeatmapCanvas.MIN_CELL_WIDTH);
    this._circle.update(safeWidth);
  }

  public getCellWidth(): number {
    return this._circle.getWidth();
  }

  public getGradient(gradientConfig?: ColorStop[]): ImageData | null {
    const gradient = new GradientCanvas({ gradientConfig });
    return gradient.getImageData();
  }

  public draw(): void {
    const ctx = this.ctx;
    const width = this.getWidth();
    const height = this.getHeight();

    if (ctx) {
      ctx.clearRect(0, 0, width, height);

      const circle = this._circle.canvas;
      const radius = this.getCellWidth() / 2;

      // draw a grayscale heatmap by putting a blurred circle at each data point
      for (let i = 0; i < this._data.length; i++) {
        const p: LatLngIntensity = this._data[i];
        const intensity = p[2] / HeatmapCanvas.MAX_INTENSITY;

        ctx.globalAlpha = Math.min(Math.max(intensity, HeatmapCanvas.MIN_OPACITY), 1);
        ctx.drawImage(circle, p[0] - radius, p[1] - radius);
      }

      if (this._gradient !== null && width > 0 && height > 0) {
        // colorize the heatmap, using opacity value of each pixel to get the right color from our gradient
        const colored = this.getImageData();
        if (colored) {
          this._colorize(colored.data, this._gradient.data);
          ctx.putImageData(colored, 0, 0);
        }
      }
    }
  }

  private _colorize(
    pixels: Uint8ClampedArray<ArrayBufferLike>,
    gradient: Uint8ClampedArray<ArrayBufferLike>,
  ): void {
    for (let i = 0; i < pixels.length; i += 4) {
      const j = pixels[i + 3] * 4; // get gradient color from opacity value

      if (j) {
        pixels[i] = gradient[j];
        pixels[i + 1] = gradient[j + 1];
        pixels[i + 2] = gradient[j + 2];
      }
    }
  }
}

export default HeatmapCanvas;
